import React, { useState, useEffect, useMemo } from "react";
import { useTable, useForm, useModal, useOrganisationContext } from "hooks";
import {
  Page,
  SubmitButton,
  FormGroup,
  PendingButton,
  DatePicker,
  LoadingMsg,
} from "components/common";
import { Table, ModalHeader, ModalBody, Input, Button, ModalFooter, Alert} from "reactstrap";
import { Trans } from "react-i18next";
import { Link } from "react-router-dom";
import { formatCurrency } from "services/util";
import { get, reduce } from "lodash";
import { NotificationManager } from "react-notifications";
import moment from "moment";
import { withDocumentRenderer } from "components";
import PayrunSummaryDocument from "components/documents/PayrunSummary";
import Select from "react-select";
import { Control } from "react-redux-form";
import PayRunStatus from "./PayRunStatus";
import SettingsClient from "../SettingsClient";
import { checkSettings } from "../checkSettings";
import { useConfirmModal } from "services";

const formatDate = (v) => moment(v).format("DD MMM YYYY");

const DocumentCell = withDocumentRenderer({
  orientation: "Landscape",
  footer: false,
  onDownload:
    ({ client, payroll }) =>
      (getDocument, item) =>
        getDocument(
          {
            item,
            client,
            payroll,
          },
          `payrun_${item.serial_number}.pdf`
        ),
})(({ DownloadDocument, DocumentRenderer, handleClick, i, selectedOrganisation }) => (
  <td className="text-end">
    <div className="d-flex justify-content-center">
      {i.status === "Draft" &&
        <>
          <Button className="ml-0 mr-1" color="success" size="sm" onClick={() => handleClick(i.id, "Approve")}><span className="fa fa-check" style={{ fontSize: 15, marginTop: "0.15em" }}></span></Button>
          <Button className="ml-0 mr-1" color="danger" size="sm" onClick={() => handleClick(i.id, "Delete")}><span className="fa fa-times" style={{ fontSize: 15, marginTop: "0.15em" }}></span></Button>
        </>
      }
      <DownloadDocument emptyButton={i.status === "Draft"} item={i} selectedOrganisation={selectedOrganisation} />
      <DocumentRenderer component={PayrunSummaryDocument} />
    </div>
  </td>
));

const MONTHS = [
  "January",
  "February",
  "March",
  "April",
  "May",
  "June",
  "July",
  "August",
  "September",
  "October",
  "November",
  "December",
];
function concatMonthWithYear(t, year) {
  return MONTHS.map((month, idx) => ({
    label: `${t(month)} ${year}`,
    month: idx + 1,
    year,
  }));
}
const UnscheduledForm = ({ t, onClose, onSubmit: onSubmitProp }) => {
  const { Form, dispatch, actions } = useForm();
  const unscheduledPayRunOptions = useMemo(() => {
    const currentYear = moment().year();
    const lastYear = currentYear - 1;
    const monthsCurrentYear = concatMonthWithYear(t, currentYear);
    const monthsLastYear = concatMonthWithYear(t, lastYear);
    const concatedYears = monthsCurrentYear.concat(monthsLastYear);
    return concatedYears
  }, [MONTHS, t]);
  const [startDate, setStartDate] = useState();
  async function onSubmit({ period }) {
    const month = new Date(period.toString()).getMonth() + 1;
    const year = new Date(period.toString()).getFullYear();
    try {
      await onSubmitProp({ month: month, year: year });
      onClose();
    } finally {
      dispatch(actions.setPending("local", false));
    }
  }

  return (
    <Form
      onSubmit={onSubmit}
      validators={{
        period: (v) => v,
      }}
    >
      <FormGroup label={t("Pay Period")} addControlClass={false} model=".period" className="fix-date-picker-modal">
        {/*<Control.text*/}
        {/*  model=".period"*/}
        {/*  component={Select}*/}
        {/*  options={unscheduledPayRunOptions}*/}
        {/*/>*/}
        <DatePicker type="date"
          model=".period"
          showYearDropdown={true}
          selected={startDate}
          onChange={(date) => { setStartDate(date); console.log(startDate) }}
          dateFormat="YYYY-MMMM"
          showMonthYearPicker={true}
          showFullMonthYearPicker={true}
          className="form-control"
        />
      </FormGroup>
      <div className="d-flex justify-content-between mt-4 mb-3">
        <PendingButton onClick={onClose} outline className="mx-0">
          Close
        </PendingButton>
        <SubmitButton className="mx-0"><Trans>Create</Trans></SubmitButton>
      </div>
    </Form>
  );
};

export const List = ({
  match,
  url = "pay_runs",
  payroll,
  history,
  t,
  client,
}) => {
  const {
    PayrunSearch,
    TableContainer,
    data,
    PayrollTableDataFetch,
    BulkDelet,
    TableSearch,
    ViewingRecords,
    Pagination,
    EditDeleteButtons,
    onDeleteItem,
    filters,
    refreshData,
  } = useTable({
    url,
    match,
    limit: 25,
  });

  const { selectedOrganisation } = useOrganisationContext()
  
  const { Form, dispatch, actions, setValue } = useForm();
  const { Modal, isOpen, setIsOpen } = useModal();
  const [nextRun, setNextRun] = useState();
  const [selected, setSelected] = useState({});
  const [setModalOpen, toggle, MainModal, setCallback] = useConfirmModal()
  const [setDeleteModalOpen, toggleAction, DeleteModal, setBulkActionCallback] = useConfirmModal();

  const [settingsData, setSettings] = useState();
  const [pending, setPending] = useState(false);
  const [delApprove, setActionID] = useState();
  const [message, setMessage] = useState();

  const loading = !settingsData || Object.keys(settingsData).length === 0;

  const settingsCheck = checkSettings(settingsData)

  const [SetApproveModal, ApproveToggle, ApproveModal, setApproveCallback] = useConfirmModal(false)
  const [setPayrunConfirmModal, payrunConfirmToggle, PayrunConfirmModal, confirmPayrun] = useConfirmModal(false)
  const [emptyFields, setEmptyFields] = useState(null)

  const [clonePayrun, setClonePayrun] = useState(null)
  
  const clearClose = () => {
    setIsOpen(false)
    setActionID(undefined)
  }

  useEffect(() => {
    (async () => {
      const nextRun = await payroll.get("pay_runs/next_pay_run").get("data");
      setNextRun(nextRun);
      setValue({ period: "CURRENT" });
    })();
  }, []);

  const payRunOptions = useMemo(() => {
    if (!nextRun) return [];
    const { month, year } = nextRun;

    return [
      { label: `${t(MONTHS[month - 1])} ${year}`, value: "CURRENT" },
      { label: t("Unscheduled Pay Run"), value: "UNSCHEDULED" },
    ];
  }, [nextRun, t]);

  const selectedIds = useMemo(() => {
    return reduce(
      selected,
      (acc, i, key) => {
        if (i) {
          acc.push(parseInt(key, 10));
        }
        return acc;
      },
      []
    );
  }, [selected]);

  async function onCreateDraft({ month, year, force = false }) { // Forcibly create payrun if it exists
    try {
      const data = await payroll
        .post("pay_runs/create_draft", {
          month,
          year,
          clone: selectedIds.length === 1 ? selectedIds[0] : null,
          force,
        })
        .get("data");
      history.push(`${match.url}/${data.id}`);
    } catch (ex) {
      const code = get(ex, "response.data.code");

      if (code === "pay_run_exists") {
        setMessage(ex?.response?.data?.detail)
        preOnConfirmPayrunSubmit({ month, year })
      } else if (code === "DraftPayRunAlreadyExists") {
        NotificationManager.error(t("Cannot have more than 1 draft pay run"));
        ex.handled = true;
        throw ex;
      } else {
        throw ex;
      }
    } finally {
      dispatch(actions.setPending("local", false));
    }
  }

  const preOnConfirmPayrunSubmit = (period) => {
    confirmPayrun(() => () => { onCreateDraft({...period, force: true}) })
    payrunConfirmToggle()
  }

  const preOnSubmit = (period) => {
    if (selectedIds.length === 1){ 
      setClonePayrun(data.results.find(i => i.id === selectedIds[0]))

      setCallback(()=>() =>{onSubmit(period)})
      toggle()
    }else{
      onSubmit(period)
    }
   }

  async function onSubmit({ period }) {
    if (period === "CURRENT") {
      return onCreateDraft(nextRun);
    } else if (period === "UNSCHEDULED") {
      setIsOpen(true);
      dispatch(actions.setPending("local", false));
    }
  }

  const settingsLink = () => {
    history.push(`/payroll/settings`)
  };

  async function handleAPI(skip=false) {

    if (!delApprove) {
      NotificationManager.warning(t("The ID cannot be selected"))
      return
    }

    setPending(true);

    if (delApprove.action === "Delete") {

      try {
        await payroll.delete(`pay_runs/${delApprove.id}`);
        NotificationManager.success(t("Pay run successfully deleted"));
        refreshData()
      } finally {
        setPending(false);
        clearClose()
      }
    } else if (delApprove.action === "Approve") {
      try {
        await payroll.post(`pay_runs/${delApprove.id}/approve?skip=${skip}`);
        SetApproveModal(false)
        NotificationManager.success(t("Pay run successfully approved"));
        refreshData()
      } catch (e) {
        if(e.response?.data?.code === 1){
          setApproveCallback(() => () => { setPending(true); handleAPI(skip=true) })
          setEmptyFields(e.response?.data?.message)
          SetApproveModal(true)
        }else{
          NotificationManager.error(`${t('Error while approving')} ${e.response?.data?.location} > ${e.response?.data?.message}`);
        }      }
      finally {
        setPending(false);
        clearClose()
      }
    }

  }

  async function handleClick(id, action) {

    const details = {
      id: id,
      action: action
    }

    setActionID(details);
    setIsOpen(true)
  };

  async function onBulkActionHandler() {
    setPending(true);

    try {
      await payroll.post(`${url}/bulk-approve`, { ids: selectedIds });
      NotificationManager.success(t("Payruns are successfully Approved"));
      setSelected({});
      refreshData()
    } finally {
      setPending(false);
    }
  }

  return (
    <Page title={t("Pay your employees")} className="inv-table container-fluid">
      <SettingsClient setSettings={setSettings} />
      {isOpen && (
        <Modal>
          <ModalHeader>
            {delApprove ? <Trans>Confirm {delApprove.action}</Trans> : <Trans>Unscheduled Pay Run</Trans>}
          </ModalHeader>
          <ModalBody>
            {delApprove ?
              <div>
                <Trans>Are you sure you want to {delApprove.action} this Payrun?</Trans>
              </div>
              :
              <UnscheduledForm
                t={t}
                onClose={() => setIsOpen(false)}
                onSubmit={onCreateDraft}
              />
            }
          </ModalBody>
          {delApprove &&
            <ModalFooter>
              <Button className="mr-0" onClick={() => clearClose()}><Trans>Close</Trans></Button>
              <Button color={delApprove.action === "Delete" ? "danger" : "success"} disabled={pending} onClick={() => handleAPI()}>{pending ? <><span className="fa fa-spinner fa-spin" /> <Trans>Confirming</Trans></> : t("Confirm")}</Button>
            </ModalFooter>}
        </Modal>
        
      )}

      {!loading ? (
        <>
          {settingsCheck.allFieldsFilled === true ? (
            <>
            <div className="row">
              <div className="col-6">
                <div className="d-flex mt-2">
                    <PendingButton
                      pending={pending}
                      onClick={() => {
                        toggleAction();
                        setBulkActionCallback(() => () => onBulkActionHandler());
                      }}
                      color="primary"
                      disabled={!selectedIds.length}
                    >
                      Bulk Approve
                    </PendingButton>
                  </div>
                </div>
              <div className="col-6">
                <Form
                  onSubmit={preOnSubmit}
                  validators={{
                    period: (v) => v,
                  }}
                >
                  <div className="mb-2 d-flex justify-content-end align-items-center">
                    <span className="mr-2">
                      {selectedIds.length === 1 ? (
                        <Trans>Clone Selected Pay Run:</Trans>
                      ) : (
                        <Trans>New Pay Run:</Trans>
                      )}
                    </span>
                    <Control.text
                      model=".period"
                      component={Select}
                      options={payRunOptions}
                      simpleValue
                      style={{ width: "12rem" }}
                      className="mr-2"
                      clearable={false}
                    />
                    <SubmitButton>{ selectedIds.length === 1 ? "Clone Pay Run" : "Process Pay Run"}</SubmitButton>
                  </div>
                </Form>
              </div>
            </div>
              <PayrunSearch label={t("Search by Status, Month, Year, Cost, Pay")} className={"mb-2 mt-3"} />

              <DeleteModal title={t("Confirm Approve")} action={t("Approve")} color="primary">
                <h6 className="font-weight-normal">
                  <Trans>
                    You have selected {{ num: selectedIds.length }} items to approve.
                  </Trans>
                </h6>
              </DeleteModal>

              <TableContainer>
                <Table className="table-container table no-wrap v-middle text-center" >
                  <thead>
                    <tr>
                      <th />
                      <th className="text-nowrap">
                        <Trans>Serial Number</Trans>
                      </th>
                      <th className="text-nowrap">
                        <Trans>Pay Period</Trans>
                      </th>
                      <th className="text-nowrap">
                        <Trans>Start Date</Trans>
                      </th>
                      <th className="text-nowrap">
                        <Trans>End Date</Trans>
                      </th>
                      <th className="text-nowrap">
                        <Trans>Total Cost</Trans>
                      </th>
                      <th className="text-nowrap">
                        <Trans>Total Pay</Trans>
                      </th>
                      <th className="text-nowrap">
                        <Trans>Status</Trans>
                      </th>
                      <th className="text-nowrap">
                        <Trans>Actions</Trans>
                      </th>
                    </tr>
                  </thead>
                  <tbody>
                    {data.results.map((i) => (
                      <tr key={i.id}>
                        <td>
                          <input
                            type="checkbox"
                            onChange={(e) => {
                              const { checked } = e.target;

                              setSelected((v) => ({
                                ...v,
                                [i.id]: checked,
                              }));
                            }}
                            checked={selected[i.id] || false}
                          />
                        </td>
                        <td>{i.serial_number}</td>
                        <td >
                          <Link to={`${match.url}/${i.id}`}>
                            <Trans>Monthly ending</Trans> {formatDate(i.end_date)}
                          </Link>
                        </td>
                        <td>{formatDate(i.start_date)}</td>
                        <td>{formatDate(i.end_date)}</td>
                        <td>{formatCurrency(i.total_cost)}</td>
                        <td>{formatCurrency(i.total_pay)}</td>
                        <td>
                          <PayRunStatus status={i.status} small />
                        </td>
                        <DocumentCell i={i} client={client} payroll={payroll} handleClick={handleClick} selectedOrganisation={selectedOrganisation} />
                      </tr>
                    ))}
                  </tbody>
                </Table>
              </TableContainer>
              <p className="form-text text-muted">
                <Trans>
                  To clone a previous pay run, tick a row above and click Process Pay
                  Run
                </Trans>
              </p>
            </>
          ) : (
            <div className="alert alert-danger fade show my-3">
              <ul className="mb-0 pl-3 text-center">
                <span style={{ cursor: "pointer" }} onClick={settingsLink}><Trans>Please Setup Payroll Accounts and It's Settings to Schedule Payruns and Payment Reports.</Trans></span>
              </ul>
            </div>
          )}
        </>) : (
        <LoadingMsg />
      )}
      <PayrollTableDataFetch />
      {/* <PayrollTableDataFetch filters={filters} /> */}
      <div className="d-flex justify-content-between">
        <div className=""><ViewingRecords noCount /></div>
        <div className=""><Pagination /></div>
      </div>

      <MainModal title={t('Cloning Confirmation')} action={t("Confirm")}>
            <Alert color="warning">
              <Trans>Are you sure you want to clone the selected payrun?</Trans> 
            </Alert>
            <Table bordered>
              <tbody>
                <tr>
                  <th><Trans>Serial Number</Trans></th>
                  <td>{clonePayrun?.serial_number}</td>
                </tr>
              </tbody>
            </Table>
      </MainModal>
      <ApproveModal title={t('Empty Values Confirmation')} action={t("Confirm")} pending={pending}>
            <Alert color="warning">
              <Trans>The following employees have have empty values in their payslips, Do you want to skip the empty values?</Trans>
            </Alert>
            { emptyFields &&
            <div className="px-2">
              <p className="text-line">{emptyFields}</p>
          </div>
          }
        </ApproveModal>

      <PayrunConfirmModal title={t('Payrun Create Confirmation')} action={t("Yes")} pending={pending}>
        <Alert color="warning">
          <Trans>{{ message }}</Trans>
        </Alert>
      </PayrunConfirmModal>
    </Page>
  );
};

export default List;
